'use client';

import { useKonamiCode } from '@/hooks/useKonamiCode';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

export const KonamiCode = () => {
  const {
    state,
    showDialog,
    getCurrentHint,
    closeDialog,
    getEasterEggContent,
    sequenceProgress
  } = useKonamiCode();

  const content = getEasterEggContent();

  // Prevent background scrolling when dialog is open
  useEffect(() => {
    if (showDialog) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showDialog]);

  return (
    <>
      {/* Progress Indicator */}
      <AnimatePresence>
        {state.isActive && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="konami-hint-popup fixed top-4 right-4 z-50 bg-card/90 backdrop-blur-sm border border-border rounded-lg max-w-sm"
          >
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <p className="konami-hint-title text-sm font-medium text-primary mb-1">🎮 Konami Code</p>
                <p className="konami-hint-text text-xs text-muted-foreground mb-3">{getCurrentHint()}</p>

                {/* Progress Bar */}
                <div className="w-full bg-muted rounded-full h-1.5">
                  <motion.div
                    className="bg-primary h-1.5 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${sequenceProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>

              <div className="text-2xl animate-pulse ml-2">
                🕹️
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Easter Egg Full-Screen Overlay - Custom implementation matching v1 */}
      <AnimatePresence>
        {showDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-8 md:p-8 sm:p-4"
            style={{
              background: 'rgba(0, 0, 0, 0.9)',
              backdropFilter: 'blur(10px)'
            }}
            onClick={closeDialog}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className="relative w-full overflow-y-auto"
              style={{
                maxWidth: '500px',
                maxHeight: '90vh',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                border: '2px solid var(--primary)',
                borderRadius: '12px',
                padding: '2rem',
                textAlign: 'center',
                boxShadow: '0 0 30px rgba(14, 165, 233, 0.5)',
                animation: 'konamiPulse 2s ease-in-out infinite alternate'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2
                className="text-2xl md:text-2xl sm:text-xl font-bold mb-6"
                style={{
                  color: 'var(--primary)',
                  textShadow: '0 0 10px rgba(14, 165, 233, 0.5)'
                }}
              >
                {content.title}
              </h2>

              <div className="space-y-4 text-foreground">
                {content.stats.map((stat, index) => (
                  <div
                    key={index}
                    className={index === 0 ?
                      "text-accent font-bold text-lg md:text-lg sm:text-base mb-4" :
                      "text-sm md:text-sm sm:text-xs leading-relaxed pl-4"
                    }
                    style={{
                      color: index === 0 ? 'var(--accent)' : 'var(--foreground)',
                      lineHeight: '1.6'
                    }}
                  >
                    {stat}
                  </div>
                ))}

                <div
                  className="mt-6 text-sm md:text-sm sm:text-xs italic"
                  style={{ color: 'var(--muted-foreground)' }}
                >
                  Press ESC to close
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
